import classNames from 'classnames';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import { DownloadButton } from '@/pages/UserAccount/UserRequests/ui';

import { useArtifactLoader } from '@/hooks/useArtifactLoader';

import { ResponseInsuranceOrder } from '@/modules/bia/shared/model/types';

import { formatDate } from '@/helpers/dates';

import api from '@/api';

import { ButtonVariant } from '../../Button';
import { Typography } from '../../Typography';
import { BaseCard, BaseServiceRowCardProps, ServiceRowCardVariant } from '../model';

import { Row, RowRenderer } from './RowRenderer';

export interface InsuranceOrderCardProps {
  variant: ServiceRowCardVariant.INSURANCE_ORDER;
  insuranceOrder: ResponseInsuranceOrder;
}

export const InsuranceOrderCard: React.FC<InsuranceOrderCardProps> = ({ insuranceOrder, variant }) => {
  const { isArtifactLoading, downloadArtifact } = useArtifactLoader((artifactType) =>
    api.downloadUserRequestArtifact(insuranceOrder.order_id, artifactType),
  );

  const { showInformer, daysDifference } = useMemo<{
    showInformer: boolean;
    daysDifference: number;
  }>(() => {
    const today = dayjs().startOf('day');
    const dateTo = dayjs(insuranceOrder.date_to).startOf('day');

    const daysDiff = dateTo.diff(today, 'day');
    const isWithin30Days = daysDiff <= 30;

    return {
      showInformer: isWithin30Days,
      daysDifference: daysDiff,
    };
  }, [insuranceOrder]);

  const informer = useMemo<NonNullable<BaseServiceRowCardProps['informerData']>>(
    () => ({
      icon: {
        name: 'attention',
        color: 'red',
      },
      header: {
        text: `Зверніть увагу! Ваш договір закінчується через ${daysDifference} днів`,
        color: 'red',
      },
      bgColor: 'white',
      borderColor: 'none',
    }),
    [daysDifference],
  );

  const rows1: Row[] = [
    {
      label: 'Номер договору',
      value: insuranceOrder.number,
      dataTestId: '',
    },
    {
      label: 'Дата укладення',
      value: formatDate(insuranceOrder.contracted_at),
      dataTestId: '',
    },
    {
      label: 'Дата початку дії',
      value: formatDate(insuranceOrder.date_from),
      dataTestId: '',
    },
  ];

  const rows2: Row[] = [
    {
      label: 'Дата завершення',
      value: formatDate(insuranceOrder.date_to),
      dataTestId: '',
    },
  ];

  return (
    <BaseCard
      data-testid="insurance-order-card"
      className={classNames('service-row-card', `service-row-card--${variant}`)}
      header={<Typography variant="H4">Договір страхування</Typography>}
      body={
        <>
          <div className="service-row-card__meta-wrapper">
            <RowRenderer rows={rows1} />
          </div>
          <div className="service-row-card__meta-wrapper">
            <RowRenderer rows={rows2} />
          </div>
        </>
      }
      informer={showInformer ? informer : null}
      actions={[
        ...(insuranceOrder.artifacts || []).map((artifactType) => (
          <DownloadButton
            key={artifactType}
            type={artifactType}
            iconName="download"
            buttonVariant={ButtonVariant.GHOST}
            disabled={isArtifactLoading}
            serviceCode={null}
            onClick={() => downloadArtifact(artifactType)}
          />
        )),
      ]}
    />
  );
};
